import { useCallback, useRef, useState } from "react"
import api from "../lib/api"
import { addDays } from "../lib/dates"
import { estimateHours } from "../lib/hours"

let temporaryId = 0
const nextTemporaryId = () => `tmp-${++temporaryId}`

const errorMessage = error =>
  (error && error.message && !/JSON|Unexpected token|Failed to fetch/i.test(error.message))
    ? error.message
    : I18n.t("project_planning.toasts.error")

const useBoard = ({ initial, urls, notify }) => {
  const [board, setBoard]     = useState(initial)
  const [loading, setLoading] = useState(false)
  const boardRef   = useRef(board)
  boardRef.current = board
  const lastLoad   = useRef(0)

  const setAssignments = useCallback(update => {
    setBoard(current => ({ ...current, assignments: update(current.assignments) }))
  }, [])

  const load = useCallback((from, to) => {
    const ticket = ++lastLoad.current
    setLoading(true)
    return api.get(`${urls.json}?from=${from}&to=${to}`)
      .then(data => { if (ticket === lastLoad.current) setBoard(data) })
      .catch(error => notify(errorMessage(error), "error"))
      .finally(() => { if (ticket === lastLoad.current) setLoading(false) })
  }, [urls.json, notify])

  const reload = useCallback(() => load(boardRef.current.from, boardRef.current.to), [load])

  const project = id => boardRef.current.projects.find(item => item.id === id)

  const replaceTemporaries = (temporaryIds, saved) => setAssignments(list => [
    ...list.filter(item => !temporaryIds.includes(item.id)),
    ...saved,
  ])

  const create = useCallback(({ projectId, userIds, dates, attrs = {} }) => {
    const target   = project(projectId)
    const settings = boardRef.current.settings
    const drafts   = userIds.flatMap(userId => dates.map(date => ({
      id: nextTemporaryId(), pending: true, user_id: userId, project_id: projectId, date,
      task_id: attrs.project_task_id || null,
      task_title: attrs.project_task_id ? target?.tasks.find(task => task.id === Number(attrs.project_task_id))?.title : null,
      hours: estimateHours(attrs, settings), planned_hours: attrs.planned_hours || null,
      starts_at: attrs.starts_at || null, ends_at: attrs.ends_at || null, note: attrs.note || null,
      color: target?.color,
    })))
    const draftIds = drafts.map(draft => draft.id)
    setAssignments(list => [...list, ...drafts])

    return api.post(urls.assignments, { project_id: projectId, user_ids: userIds, dates, assignment: attrs })
      .then(({ assignments }) => {
        replaceTemporaries(draftIds, assignments)
        notify(I18n.t("project_planning.toasts.created", { count: assignments.length }))
        return assignments
      })
      .catch(error => {
        replaceTemporaries(draftIds, [])
        notify(errorMessage(error), "error")
      })
  }, [urls.assignments, notify, setAssignments])

  // userId undefined = keep the person.
  const move = useCallback((ids, { dayOffset = 0, userId, projectId }) => {
    const before = boardRef.current.assignments.filter(item => ids.includes(item.id))
    const moved  = before.map(item => ({
      ...item,
      pending: true,
      date:       addDays(item.date, dayOffset),
      user_id:    userId === undefined ? item.user_id : userId,
      project_id: projectId || item.project_id,
      color:      projectId && projectId !== item.project_id ? project(projectId)?.color : item.color,
      task_id:    projectId && projectId !== item.project_id ? null : item.task_id,
      task_title: projectId && projectId !== item.project_id ? null : item.task_title,
    }))
    setAssignments(list => list.map(item => moved.find(change => change.id === item.id) || item))

    return Promise.all(moved.map(item => api.patch(`${urls.assignments}/${item.id}`, {
      date:    item.date,
      user_id: item.user_id == null ? "" : item.user_id,
      ...(projectId ? { project_id: projectId } : {}),
    })))
      .then(results => {
        const saved = results.map(result => result.assignment)
        setAssignments(list => list.map(item => saved.find(change => change.id === item.id) || item))
        notify(I18n.t("project_planning.toasts.moved", { count: saved.length }))
      })
      .catch(error => {
        setAssignments(list => list.map(item => before.find(previous => previous.id === item.id) || item))
        notify(errorMessage(error), "error")
        reload()
      })
  }, [urls.assignments, notify, setAssignments, reload])

  const duplicate = useCallback((ids, { dayOffset = 0, userId }) => {
    const sources = boardRef.current.assignments.filter(item => ids.includes(item.id))
    const drafts  = sources.map(item => ({
      ...item, id: nextTemporaryId(), pending: true,
      date: addDays(item.date, dayOffset), user_id: userId === undefined ? item.user_id : userId,
    }))
    const draftIds = drafts.map(draft => draft.id)
    setAssignments(list => [...list, ...drafts])

    const body = { ids, day_offset: dayOffset }
    if (userId !== undefined) body.user_id = userId == null ? "" : userId
    return api.post(urls.duplicate, body)
      .then(({ assignments }) => {
        replaceTemporaries(draftIds, assignments)
        notify(I18n.t("project_planning.toasts.duplicated", { count: assignments.length }))
      })
      .catch(error => {
        replaceTemporaries(draftIds, [])
        notify(errorMessage(error), "error")
      })
  }, [urls.duplicate, notify, setAssignments])

  const update = useCallback((id, { projectId, attrs }) => {
    const before = boardRef.current.assignments.find(item => item.id === id)
    if (!before) return Promise.resolve()
    const target  = project(projectId || before.project_id)
    const optimistic = {
      ...before, pending: true, project_id: target.id,
      task_id: attrs.project_task_id ? Number(attrs.project_task_id) : null,
      task_title: attrs.project_task_id ? target.tasks.find(task => task.id === Number(attrs.project_task_id))?.title : null,
      planned_hours: attrs.planned_hours || null, starts_at: attrs.starts_at || null, ends_at: attrs.ends_at || null,
      hours: estimateHours(attrs, boardRef.current.settings), note: attrs.note || null,
      color: target.color,
    }
    setAssignments(list => list.map(item => item.id === id ? optimistic : item))

    const body = { assignment: attrs }
    if (projectId && projectId !== before.project_id) body.project_id = projectId
    return api.patch(`${urls.assignments}/${id}`, body)
      .then(({ assignment }) => {
        setAssignments(list => list.map(item => item.id === id ? assignment : item))
        notify(I18n.t("project_planning.toasts.updated"))
      })
      .catch(error => {
        setAssignments(list => list.map(item => item.id === id ? before : item))
        notify(errorMessage(error), "error")
      })
  }, [urls.assignments, notify, setAssignments])

  const remove = useCallback(ids => {
    const removed = boardRef.current.assignments.filter(item => ids.includes(item.id))
    const saved   = removed.filter(item => typeof item.id === "number").map(item => item.id)
    setAssignments(list => list.filter(item => !ids.includes(item.id)))

    return api.post(urls.destroyMany, { ids: saved })
      .then(({ destroyed }) => notify(I18n.t("project_planning.toasts.deleted", { count: destroyed })))
      .catch(error => {
        setAssignments(list => [...list, ...removed])
        notify(errorMessage(error), "error")
      })
  }, [urls.destroyMany, notify, setAssignments])

  const copyWeek = useCallback((from, to) =>
    api.post(urls.copyWeek, { from, to })
      .then(({ assignments }) => {
        notify(I18n.t("project_planning.toasts.week_copied", { count: assignments.length }))
        return reload()
      })
      .catch(error => notify(errorMessage(error), "error")),
  [urls.copyWeek, notify, reload])

  return { board, loading, load, reload, actions: { create, move, duplicate, update, remove, copyWeek } }
}

export default useBoard
